import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import StepHeading from './StepHeading';
import WizardFooter from './WizardFooter';

// reCAPTCHA Typen
declare global {
  interface Window {
    grecaptcha?: {
      ready?: (callback: () => void) => void;
      execute?: (siteKey: string, options: { action: string }) => Promise<string>;
      reset?: (widgetId?: number) => void;
      render?: (container: string | HTMLElement, options: any) => number;
    };
    ISK_RECAPTCHA_SITE_KEY: string;
  }
}

interface ContactFormProps {
  onBack: () => void;
  onSubmit: (values: any) => void;
  isSubmitting: boolean;
  selectedProducts?: any;
}

const validationSchema = yup.object({
  vorname: yup.string().required('Vorname ist erforderlich'),
  nachname: yup.string().required('Nachname ist erforderlich'),
  email: yup.string().email('Ungültige E-Mail-Adresse').required('E-Mail ist erforderlich'),
  telefon: yup.string().required('Telefonnummer ist erforderlich'),
  datenschutz: yup.boolean().oneOf([true], 'Bitte akzeptieren Sie die Datenschutzerklärung')
});

const benefitItems = [
  'Detailliertes PDF-Angebot',
  '5 % Rabatt bereits eingerechnet',
  'Kostenloser Vor-Ort-Termin',
  'Unverbindlich, keine Zahlung online',
];

const ContactForm: React.FC<ContactFormProps> = ({ onBack, onSubmit, isSubmitting, selectedProducts }) => {
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const [widgetId, setWidgetId] = useState<number | null>(null);
  const selectedProductCount = Array.isArray(selectedProducts) ? selectedProducts.length : 0;

  useEffect(() => {
    // Script manuell laden
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // reCAPTCHA Widget rendern
      if (recaptchaRef.current && window.grecaptcha?.render) {
        try {
          const id = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: window.ISK_RECAPTCHA_SITE_KEY,
            theme: 'light',
            size: 'normal',
            callback: () => {
              setRecaptchaError(null);
            },
            'expired-callback': () => {
              setRecaptchaError('reCAPTCHA ist abgelaufen. Bitte erneut bestätigen.');
              if (window.grecaptcha?.reset && widgetId !== null) {
                window.grecaptcha.reset(widgetId);
              }
            }
          });
          setWidgetId(id);
        } catch (error) {
          console.error('Fehler beim Rendern des reCAPTCHA:', error);
          setRecaptchaError('reCAPTCHA konnte nicht initialisiert werden');
        }
      }
    };

    script.onerror = () => {
      setRecaptchaError('reCAPTCHA konnte nicht geladen werden');
      console.error('Fehler beim Laden des reCAPTCHA Scripts');
    };

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      if (widgetId !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(widgetId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      vorname: '',
      nachname: '',
      email: '',
      telefon: '',
      datenschutz: false
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // reCAPTCHA Response holen
        const recaptchaResponse = document.querySelector<HTMLInputElement>('[name="g-recaptcha-response"]')?.value;

        if (!recaptchaResponse) {
          setRecaptchaError('Bitte bestätigen Sie, dass Sie kein Roboter sind.');
          return;
        }

        const submitData = {
          ...values,
          'g-recaptcha-response': recaptchaResponse
        };

        await onSubmit(submitData);
      } catch (error) {
        console.error('Fehler beim Absenden:', error);
        setRecaptchaError('Ein Fehler ist beim Absenden aufgetreten');
        if (widgetId !== null && window.grecaptcha?.reset) {
          window.grecaptcha.reset(widgetId);
        }
      }
    },
  });

  const textFieldProps = (name: keyof typeof formik.values, placeholder: string) => ({
    fullWidth: true,
    id: name,
    name,
    placeholder,
    value: formik.values[name],
    onChange: formik.handleChange,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && (formik.errors[name] as string),
  });

  return (
    <Box>
      <StepHeading step={4} title="Angebot per E-Mail erhalten" />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          bgcolor: '#E8F1FB',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Box
          sx={{
            bgcolor: '#C07C1D',
            color: '#fff',
            fontWeight: 800,
            borderRadius: '8px',
            px: 1.25,
            py: 0.75,
            fontSize: '0.9rem',
            flexShrink: 0,
          }}
        >
          -5%
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Ihr Online-Vorteil — 5 % Rabatt</Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 2.5,
              rowGap: 0.5,
              mt: 0.75,
            }}
          >
            {benefitItems.map(item => (
              <Typography key={item} variant="body2" sx={{ color: 'text.primary' }}>
                ✓ {item}
              </Typography>
            ))}
          </Box>
          {selectedProductCount > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              Ihr Angebot wird auf Basis Ihrer {selectedProductCount} Position
              {selectedProductCount === 1 ? '' : 'en'} erstellt und direkt per E-Mail verschickt.
            </Typography>
          )}
        </Box>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField {...textFieldProps('vorname', 'Vorname')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...textFieldProps('nachname', 'Nachname')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...textFieldProps('email', 'E-Mail-Adresse')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...textFieldProps('telefon', 'Telefon')} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  id="datenschutz"
                  name="datenschutz"
                  checked={formik.values.datenschutz}
                  onChange={formik.handleChange}
                />
              }
              label={
                <Typography variant="body2">
                  Ich habe die <Link href="/datenschutz" target="_blank">Datenschutzerklärung</Link> gelesen und akzeptiere diese.
                </Typography>
              }
            />
            {formik.touched.datenschutz && formik.errors.datenschutz && (
              <Typography color="error" variant="caption" display="block">
                {formik.errors.datenschutz as string}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <div ref={recaptchaRef}></div>
          </Grid>
        </Grid>

        {recaptchaError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {recaptchaError}
          </Typography>
        )}

        <WizardFooter
          onBack={onBack}
          nextLabel="Angebot mit 5 % Rabatt anfordern"
          nextType="submit"
          isSubmitting={isSubmitting}
        />
      </form>
    </Box>
  );
};

export default ContactForm;
